import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Card, Alert } from 'react-bootstrap';
import { RiLockPasswordLine } from "react-icons/ri";
import { MdCheckCircle } from "react-icons/md";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import {
  CustomInput,
  CustomSubmitButton,
  CustomFormStyles
} from './CustomFormComponents'; // Adjust path as needed
import { resetPassword } from '../../../store/auth'; // Adjust path as needed

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength validation states
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    specialChar: false
  });

  // Check if token exists when component mounts
  useEffect(() => {
    if (!token) {
      setTokenError(true);
    }
  }, [token]);

  // Update password validation in real-time
  useEffect(() => {
    const password = formData.newPassword;
    setPasswordValidation({
      length: password.length >= 8,
      lowercase: /(?=.*[a-z])/.test(password),
      uppercase: /(?=.*[A-Z])/.test(password),
      number: /(?=.*\d)/.test(password),
      specialChar: /(?=.*[@$!%*?&#])/.test(password)
    });
  }, [formData.newPassword]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate new password
    if (!formData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one lowercase letter';
    } else if (!/(?=.*[A-Z])/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter';
    } else if (!/(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one number';
    } else if (!/(?=.*[@$!%*?&#])/.test(formData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one special character (@$!%*?&#)';
    }

    // Validate confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    
    try {
      // Call API to reset password
      await resetPassword(token, formData.newPassword);
      
      // Success!
      setIsSuccess(true);
      
      // Don't auto-navigate - let user click "Go to Login" manually
      // User needs to see the success message
      
    } catch (error) {
      console.error('Reset password error:', error);
      
      let errorMessage = 'Failed to reset password. Please try again.';
      
      if (error.message) {
        if (error.message.includes('expired') || error.message.includes('invalid')) {
          errorMessage = 'This reset link has expired or is invalid. Please request a new one.';
        } else if (error.message.includes('Network')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setErrors({ general: errorMessage });
      
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/'); // Adjust path to your login page
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Password requirement item component
  const PasswordRequirement = ({ met, text }) => (
    <li style={{ 
      color: met ? '#28a745' : '#dc3545',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '6px'
    }}>
      {met ? (
        <FaCheckCircle style={{ fontSize: '14px', flexShrink: 0 }} />
      ) : (
        <FaTimesCircle style={{ fontSize: '14px', flexShrink: 0 }} />
      )}
      <span>{text}</span>
    </li>
  );

  // If no token in URL, show error
  if (tokenError) {
    return (
      <div className="d-flex align-items-center justify-content-center min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
        <Container>
          <Card className="shadow-sm mx-auto" style={{ maxWidth: '500px' }}>
            <Card.Body className="p-5 text-center">
              <div className="text-danger mb-4">
                <RiLockPasswordLine size={64} />
              </div>
              <h3 className="mb-3">Invalid Reset Link</h3>
              <p className="text-muted mb-4">
                This password reset link is invalid or has been removed. 
                Please request a new password reset link.
              </p>
              <button 
                className="btn btn-primary"
                onClick={handleBackToLogin}
              >
                Go to Login
              </button>
            </Card.Body>
          </Card>
        </Container>
        <CustomFormStyles />
      </div>
    );
  }

  // Success screen
  if (isSuccess) {
    return (
      <div className="d-flex align-items-center justify-content-center min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
        <Container>
          <Card className="shadow-sm mx-auto" style={{ maxWidth: '500px' }}>
            <Card.Body className="p-5 text-center">
              <div className="text-success mb-4">
                <MdCheckCircle size={64} />
              </div>
              <h3 className="text-success mb-3">Password Reset Successfully!</h3>
              <p className="text-muted mb-4">
                Your password has been updated successfully. 
                You can now login with your new password.
              </p>
             
              <button 
                className="btn btn-primary"
                onClick={handleBackToLogin}
              >
                Go to Login Now
              </button>
            </Card.Body>
          </Card>
        </Container>
        <CustomFormStyles />
      </div>
    );
  }

  // Reset password form
  return (
    <div className="d-flex align-items-center justify-content-center min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      <Container>
        <Card className="shadow-sm mx-auto" style={{ maxWidth: '500px' }}>
          <Card.Body className="p-5">
            <div className="text-center mb-4">
              <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" 
                   style={{ width: '80px', height: '80px' }}>
                <RiLockPasswordLine size={32} className="text-primary" />
              </div>
              <h3 className="mb-2">Reset Your Password</h3>
              <p className="text-muted">Enter your new password below</p>
            </div>

            {errors.general && (
              <Alert variant="danger" className="mb-4">
                {errors.general}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <div className="position-relative">
                <CustomInput
                  type={showNewPassword ? "text" : "password"}
                  name="newPassword"
                  label="New Password"
                  required
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder="Enter new password"
                  error={errors.newPassword}
                  autoFocus={true}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={toggleNewPasswordVisibility}
                  disabled={isLoading}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '38px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6c757d',
                    zIndex: 10
                  }}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                >
                  {showNewPassword ? (
                    <AiOutlineEyeInvisible size={20} />
                  ) : (
                    <AiOutlineEye size={20} />
                  )}
                </button>
              </div>

              <div className="position-relative">
                <CustomInput
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  label="Confirm New Password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirm new password"
                  error={errors.confirmPassword}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={toggleConfirmPasswordVisibility}
                  disabled={isLoading}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '38px',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#6c757d',
                    zIndex: 10
                  }}
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? (
                    <AiOutlineEyeInvisible size={20} />
                  ) : (
                    <AiOutlineEye size={20} />
                  )}
                </button>
              </div>

              <div className="alert alert-info mb-4" role="alert" style={{
                backgroundColor: '#f8f9fa',
                borderColor: '#dee2e6'
              }}>
                <small>
                  <strong style={{ color: '#495057' }}>Password Requirements:</strong>
                  <ul className="mb-0 mt-2 ps-3" style={{ listStyleType: 'none' }}>
                    <PasswordRequirement 
                      met={passwordValidation.length}
                      text="At least 8 characters long"
                    />
                    <PasswordRequirement 
                      met={passwordValidation.lowercase}
                      text="Contains at least one lowercase letter"
                    />
                    <PasswordRequirement 
                      met={passwordValidation.uppercase}
                      text="Contains at least one uppercase letter"
                    />
                    <PasswordRequirement 
                      met={passwordValidation.number}
                      text="Contains at least one number"
                    />
                    <PasswordRequirement 
                      met={passwordValidation.specialChar}
                      text="Contains at least one special character (@$!%*?&#)"
                    />
                  </ul>
                </small>
              </div>

              <CustomSubmitButton
                isLoading={isLoading}
                loadingText="Resetting Password..."
              >
                Reset Password
              </CustomSubmitButton>
            </form>

            
          </Card.Body>
        </Card>
      </Container>
      <CustomFormStyles />
    </div>
  );
};

export default ResetPassword;